import { useEffect, useMemo, useRef, useState } from "react";
import {
  Paper, Table, TableBody, TableCell, tableCellClasses,
  TableContainer, TableHead, TablePagination, TableRow,
  Alert, Box, CircularProgress, Grid,
  IconButton, Menu, MenuItem, styled, useTheme,Button,
  TextField, Stack, InputAdornment, Tooltip, Typography
} from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate, useLocation } from 'react-router-dom';
import { getAll } from "../../services/PatientAdmissionService";
import { useAuth } from "../../context/AuthContext.jsx";
import EditPatientModal from "../AddPatientModal/editPatientModal.tsx";
import PatientAssignmentModal from "../inPatientDetail/patientAssignment.jsx";
import PatientTreatmentModal from "../inPatientDetail/patientTreatments.jsx";
import PatientConsumableModal from "../inPatientDetail/patientConsumables.jsx";
import DoctorVisitsModal from "../inPatientDetail/doctorVisits.jsx";
import PatientOperationModal from "../inPatientDetail/patientOperations.jsx";
import PatientDischargeModal from "../inPatientDetail/patientDischarge.jsx";
import LabtestModal from "../inPatientDetail/patientLabTests.jsx";
import NurseKardex from "../inPatientDetail/nurseKardex.jsx";
import NewAdmissionModal from "../AddPatientModal/InPatientAppointmentModal.tsx";
import IpdPrescriptionModal from "../PatientInQueue/IpdPrescriptionModal.jsx";
import { getPatientById } from "../../services/patientService";
import { getAssignmentByAdmissionId } from "../../services/PatientAssignmentService";
import { getAllStaff } from "../../services/StaffService";
import { getDoctorById, getDoctorByUserId, getAllDoctors } from "../../services/doctorService";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import { getAllDepartment } from "../../services/departmentService";
import {
  buildDepartmentsLookup,
  buildDoctorsLookup,
  buildIpdSummaryRecords,
  computePatientPeriodSummary,
  formatPeriodLabel,
  parsePeriodBounds,
  pickLatestAssignment,
} from "../../utils/patientPeriodSummary";
import { downloadPatientTableExcel } from "../../utils/patientTableExcelExport";
import { getOrganizationName } from "../../utils/organizationUtils";
import { fetchAssignmentsByAdmissionIds } from "../../services/patientPeriodSummaryService";
import {
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
} from "../../utils/pageLayoutStyles";

const columns = [
  { id: "id", label: "Admission Id" },
  { id: "patientId", label: "Patient ID" },
  { id: "patientName", label: "Patient Name" },
  { id: "email", label: "Email" },
  { id: "mobile", label: "Mobile" },
  { id: "gender", label: "Gender" },
  { id: "age", label: "Age" },
  { id: "admissionDate", label: "Admission Date" },
  { id: "admissionTime", label: "Admission Time" },
  { id: "status", label: "Admission Status" },
  { id: "action", label: "" },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
}));

function PatientsTable({ onPatientClick }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [admissions, setAdmissions] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedAdmissionId, setSelectedAdmissionId] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [open_emergency_modal, setOpenEmergencyModal] = useState(null);
  const [open_lab_modal, setOpenLabModal] = useState(null);
  const [modals, setModals] = useState({
    edit: false, assignment: false, treatment: false,
    consumable: false, doctorVisit: false, operation: false,
    discharge: false, newAdmission: false, nursKardex: false, labtests: false, prescription: false
  });
  const [openNewAdmissionModal, setOpenNewAdmissionModal] = useState(false);
  const [newAdmissionPatientInfo, setNewAdmissionPatientInfo] = useState(null);
  const [isAssignedNurse, setIsAssignedNurse] = useState(false);
  const [isAssignedDoctor, setIsAssignedDoctor] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [enrichedAdmissions, setEnrichedAdmissions] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0, message: "" });
  const [enrichmentCache, setEnrichmentCache] = useState(new Set());
  const [admissionsCache, setAdmissionsCache] = useState(null);
  const [cacheTimestamp, setCacheTimestamp] = useState(null);
  const [patientDetailsMap, setPatientDetailsMap] = useState({});
  const [doctorsById, setDoctorsById] = useState({});
  const [departmentsById, setDepartmentsById] = useState({});
  const [assignmentsByAdmissionId, setAssignmentsByAdmissionId] = useState({});
  const fetchedAssignmentIdsRef = useRef(new Set());

  const location = useLocation();
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const roleName = (user?.roleName || "").toLowerCase();
  const isAdmin = roleName === "admin" || roleName === "super_admin";

  const isEmergencyConvertedIpd = (admission) => {
    if (!admission) return false;
    const admissionType = String(
      admission.admissionType || admission.admission_type || ""
    ).toLowerCase();
    const diseaseDiagnose = String(
      admission.diseaseDiagnose || admission.disease_diagnose || ""
    ).toLowerCase();
    return admissionType === "emergency" || diseaseDiagnose === "emergency";
  };

  const parseIds = (value) => {
    if (value == null) return [];
    if (Array.isArray(value)) {
      return value.map((v) => Number(v)).filter((n) => Number.isFinite(n));
    }
    if (typeof value === "string") {
      return value
        .split(",")
        .map((s) => Number((s || "").trim()))
        .filter((n) => Number.isFinite(n));
    }
    const n = Number(value);
    return Number.isFinite(n) ? [n] : [];
  };

  const isUserAssignedDoctor = async (admission) => {
    if (!admission || !user) return false;
    
    // Check if user is a doctor
    if (user.roleName !== "doctor" && user.roleName !== "surgeon") return false;
    
    try {
      // Get the doctor ID using the user ID
      const doctorDetails = await getDoctorByUserId(user.id);
      if (!doctorDetails || !doctorDetails.id) return false;
      
      const doctorId = doctorDetails.id;
      
      // Check assignment doctor ID
      if (admission.assignment?.doctorId) {
        const assignmentDoctorId = admission.assignment.doctorId;
        // Compare both as strings and numbers to handle type mismatches
        if (String(assignmentDoctorId) === String(doctorId) || Number(assignmentDoctorId) === Number(doctorId)) {
          return true;
        }
      }
      
      // Also check if the admission has a direct doctorId field
      if (admission.doctorId) {
        const admissionDoctorId = admission.doctorId;
        if (String(admissionDoctorId) === String(doctorId) || Number(admissionDoctorId) === Number(doctorId)) {
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error("Error fetching doctor details:", error);
      return false;
    }
  };

  const isUserAssignedNurse = (admission) => {
    if (!admission) return false;
    const uidNum = Number(user?.id);
    const uidStr = String(user?.id ?? "").trim();
    if (!uidStr && !Number.isFinite(uidNum)) return false;
    const rawValues = [];
    if (admission?.assignment?.nurseId != null) rawValues.push(admission.assignment.nurseId);
    if (admission?.nurseId != null) rawValues.push(admission.nurseId);
    for (const val of rawValues) {
      if (Array.isArray(val)) {
        for (const v of val) {
          if (String(v).trim() === uidStr) return true;
          if (Number.isFinite(uidNum) && Number(v) === uidNum) return true;
        }
      } else if (typeof val === "string") {
        const parts = val.split(',').map((s) => s.trim()).filter(Boolean);
        if (parts.includes(uidStr)) return true;
        if (Number.isFinite(uidNum) && parts.some((p) => Number(p) === uidNum)) return true;
      } else if (val != null) {
        if (String(val).trim() === uidStr) return true;
        if (Number.isFinite(uidNum) && Number(val) === uidNum) return true;
      }
    }
    return false;
  };

  const [staffLookup, setStaffLookup] = useState(null);
  useEffect(() => {
    const loadStaff = async () => {
      try {
        const list = await getAllStaff();
        // Build map nurseId -> userId (string compare) and also number
        const map = new Map();
        (list || []).forEach((s) => {
          const nurseId = s?.id ?? s?.staffId ?? s?.userId;
          const userId = s?.userId ?? s?.id;
          if (nurseId != null) map.set(String(nurseId), String(userId));
        });
        setStaffLookup(map);
      } catch (e) {
        setStaffLookup(null);
      }
    };
    loadStaff();
  }, []);

  const computeIsAssignedNurse = (admission, assignment) => {
    const userName = String(user?.username ?? "").trim().toLowerCase();
    const roleOk = (roleName === "staff" || roleName.includes("nurse"));
    if (!roleOk || !userName) return false;

    // 1) Prefer matching by nurseName from assignment (can be single or comma-separated)
    const rawNames = [];
    if (assignment?.nurseName != null) rawNames.push(assignment.nurseName);
    if (admission?.nurseName != null) rawNames.push(admission.nurseName);
    for (const rn of rawNames) {
      if (Array.isArray(rn)) {
        const ok = rn.some((n) => String(n).trim().toLowerCase() === userName);
        if (ok) return true;
      } else if (rn != null) {
        const parts = String(rn).split(',').map((s)=>s.trim()).filter(Boolean);
        const ok = parts.some((n) => n.toLowerCase() === userName);
        if (ok) return true;
      }
    }

    // 2) Fallback to id → userId mapping if available
    if (staffLookup && user?.id) {
      const myUserId = String(user.id);
      const rawNurse = assignment?.nurseId ?? admission?.nurseId;
      if (rawNurse != null) {
        const ids = Array.isArray(rawNurse)
          ? rawNurse.map((v)=>String(v))
          : String(rawNurse).split(',').map((s)=>s.trim()).filter(Boolean);
        for (const nurseId of ids) {
          const mappedUserId = staffLookup.get(String(nurseId));
          if (mappedUserId && String(mappedUserId) === myUserId) return true;
        }
      }
    }

    return false;
  };

  useEffect(() => {
    fetchLatestAdmissions();
  }, [location.key]);

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const departments = await getAllDepartment();
        setDepartmentsById(buildDepartmentsLookup(departments));
      } catch (error) {
        console.error("Failed to fetch departments:", error);
      }
    };
    loadDepartments();
  }, []);

  // const fetchLatestAdmissions = async () => {
  //   setFetching(true);
  //   try {
  //     const data = await getAll();
  //     const grouped = data.reduce((acc, admission) => {
  //       const existing = acc[admission.patientId];
  //       const currentDate = new Date(
  //         admission.admissionDate[0],
  //         admission.admissionDate[1] - 1,
  //         admission.admissionDate[2],
  //         admission.admissionTime[0],
  //         admission.admissionTime[1],
  //         admission.admissionTime[2]
  //       );
  //       if (
  //         !existing ||
  //         new Date(existing._date) < currentDate
  //       ) {
  //         acc[admission.patientId] = {
  //           ...admission,
  //           _date: currentDate,
  //         };
  //       }
  //       return acc;
  //     }, {});
  //     const latestAdmissions = Object.values(grouped);
  //     setAdmissions(latestAdmissions);
  //   } catch (err) {
  //     console.error(err);
  //   } finally {
  //     setFetching(false);
  //   }
  // };

  // Helper function to batch process with concurrency limit
  const batchProcess = async (items, processor, batchSize = 10) => {
    const results = [];
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      const batchResults = await Promise.allSettled(batch.map(processor));
      results.push(...batchResults.map(result => 
        result.status === 'fulfilled' ? result.value : { error: result.reason }
      ));
    }
    return results;
  };

  // Helper function to safely parse creation date from admission
  const parseCreationDate = (admission) => {
    if (admission.createdAt) return new Date(admission.createdAt).toISOString();
    if (admission.created_at) return new Date(admission.created_at).toISOString();
    if (admission.admissionDate) {
      if (Array.isArray(admission.admissionDate)) {
        return new Date(admission.admissionDate[0], admission.admissionDate[1] - 1, admission.admissionDate[2]).toISOString();
      } else if (typeof admission.admissionDate === 'string') {
        return new Date(admission.admissionDate).toISOString();
      } else if (admission.admissionDate instanceof Date) {
        return admission.admissionDate.toISOString();
      }
    }
    return null;
  };

  // Enrich a subset of admissions (for lazy loading)
  const enrichAdmissions = async (admissionsToEnrich, doctorsById, patientDetailsMap) => {
    const admissionIds = admissionsToEnrich.map(a => a.id);
    
    // Get unique patient IDs that need to be fetched
    const patientIds = admissionsToEnrich.map(a => a.patientId).filter(Boolean);
    const uniquePatientIds = Array.from(new Set(patientIds));
    const missingPatientIds = uniquePatientIds.filter(id => !patientDetailsMap[id]);
    
    // Batch fetch missing patient details
    let updatedPatientMap = { ...patientDetailsMap };
    if (missingPatientIds.length > 0) {
      const patientResults = await batchProcess(
        missingPatientIds,
        async (patientId) => {
          try {
            const patient = await getPatientById(patientId);
            return { patientId, patient };
          } catch (err) {
            return { patientId, patient: null };
          }
        },
        10
      );
      
      patientResults.forEach(({ patientId, patient }) => {
        if (patient) {
          updatedPatientMap[patientId] = patient;
        }
      });
      
      // Update patient details map
      setPatientDetailsMap(updatedPatientMap);
    }
    
    // Batch fetch assignments
    const assignmentsData = await batchProcess(
      admissionIds,
      async (admissionId) => {
        try {
          const assignment = await getAssignmentByAdmissionId(admissionId);
          return { 
            admissionId, 
            assignment: pickLatestAssignment(assignment),
          };
        } catch (err) {
          return { admissionId, assignment: null };
        }
      },
      10
    );

    // Create lookup map
    const assignmentsByAdmission = {};
    assignmentsData.forEach(({ admissionId, assignment }) => {
      if (assignment) {
        assignmentsByAdmission[admissionId] = assignment;
      }
    });

    if (Object.keys(assignmentsByAdmission).length > 0) {
      admissionIds.forEach((id) => fetchedAssignmentIdsRef.current.add(String(id)));
      setAssignmentsByAdmissionId((prev) => ({ ...prev, ...assignmentsByAdmission }));
    }

    // Enrich admissions

    return Promise.all(admissionsToEnrich.map(async (admission) => {
      try {
        let assignedDoctor = null;
        const creationDate = parseCreationDate(admission);

        const assignment = assignmentsByAdmission[admission.id];
        if (assignment?.doctorId) {
          // Try to find doctor by doctor ID first
          const doctor = doctorsById[assignment.doctorId];
          if (doctor?.name) {
            assignedDoctor = doctor.name;
          } else {
            // If not found, try getDoctorByUserId (doctorId might be a user_id)
            try {
              const doctorByUserId = await getDoctorByUserId(assignment.doctorId);
              if (doctorByUserId?.name) {
                assignedDoctor = doctorByUserId.name;
              } else {
                // Try getDoctorById as fallback
                try {
                  const doctorById = await getDoctorById(assignment.doctorId);
                  if (doctorById?.name) {
                    assignedDoctor = doctorById.name;
                  } else {
                    assignedDoctor = `Dr. ID: ${assignment.doctorId}`;
                  }
                } catch {
                  assignedDoctor = `Dr. ID: ${assignment.doctorId}`;
                }
              }
            } catch {
              // If getDoctorByUserId fails, try getDoctorById
              try {
                const doctorById = await getDoctorById(assignment.doctorId);
                if (doctorById?.name) {
                  assignedDoctor = doctorById.name;
                } else {
                  assignedDoctor = `Dr. ID: ${assignment.doctorId}`;
                }
              } catch {
                assignedDoctor = `Dr. ID: ${assignment.doctorId}`;
              }
            }
          }
        }

        return {
          ...admission,
          assignment,
          departmentId: assignment?.departmentId ?? admission.departmentId,
          assignedDoctor,
          creationDate,
        };
      } catch (err) {
        return {
          ...admission,
          assignment: assignmentsByAdmission[admission.id] || null,
          assignedDoctor: null,
          creationDate: parseCreationDate(admission),
        };
      }
    }));
  };

  const fetchLatestAdmissions = async (forceRefresh = false) => {
    setFetching(true);
    setLoadingProgress({ loaded: 0, total: 0, message: "Loading..." });
    
    try {
      // If forceRefresh is true, clear cache
      if (forceRefresh) {
        setAdmissionsCache(null);
        setCacheTimestamp(null);
      }
      
      // Check cache (5 minute TTL)
      const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
      const now = Date.now();
      const cachedData = !forceRefresh && admissionsCache && cacheTimestamp && (now - cacheTimestamp < CACHE_TTL) ? admissionsCache : null;
      
      let data;
      if (cachedData) {
        console.log("Using cached admissions data");
        data = cachedData;
        setLoadingProgress({ loaded: 0, total: 0, message: "Processing cached data..." });
      } else {
        // Step 1: Fetch all admissions
        console.log("Starting to fetch admissions from API...");
        const startTime = Date.now();
        try {
          data = await getAll();
          const fetchTime = ((Date.now() - startTime) / 1000).toFixed(1);
          console.log(`Fetched ${data?.length || 0} admissions in ${fetchTime}s`);
        } catch (err) {
          // If getAll() throws, it means it's not a table-not-found error
          // In that case, data will be undefined, so set it to empty array
          console.error("Error fetching admissions:", err);
          data = [];
        }
        
        // Ensure data is an array
        data = Array.isArray(data) ? data : [];
        
        // Cache the raw data
        setAdmissionsCache(data);
        setCacheTimestamp(now);
      }
      
      // Step 2: Optimize grouping - single pass with Map for O(n) complexity
      setLoadingProgress({ loaded: 0, total: 0, message: "Processing admissions..." });
      const dataArray = Array.isArray(data) ? data : [];
      const grouped = new Map();
      
      // Single pass grouping - much faster than chunked approach
      for (const admission of dataArray) {
        if (!admission?.patientId) continue;
        
        // Safely parse admissionDate - handle array, string, or Date object
        let currentDate;
        if (Array.isArray(admission.admissionDate)) {
          // Array format: [year, month, day]
          currentDate = new Date(
            admission.admissionDate[0],
            admission.admissionDate[1] - 1,
            admission.admissionDate[2],
            admission.admissionTime?.[0] || 0,
            admission.admissionTime?.[1] || 0,
            admission.admissionTime?.[2] || 0
          );
        } else if (admission.admissionDate) {
          // String or Date object format
          if (typeof admission.admissionDate === 'string') {
            // ISO string or date string
            currentDate = new Date(admission.admissionDate);
            // If admissionTime is provided separately, add it
            if (admission.admissionTime) {
              if (Array.isArray(admission.admissionTime)) {
                currentDate.setHours(
                  admission.admissionTime[0] || 0,
                  admission.admissionTime[1] || 0,
                  admission.admissionTime[2] || 0
                );
              } else if (typeof admission.admissionTime === 'string') {
                // Parse time string (HH:mm:ss or HH:mm)
                const timeParts = admission.admissionTime.split(':');
                if (timeParts.length >= 2) {
                  currentDate.setHours(
                    parseInt(timeParts[0]) || 0,
                    parseInt(timeParts[1]) || 0,
                    parseInt(timeParts[2]) || 0
                  );
                }
              }
            }
          } else if (admission.admissionDate instanceof Date) {
            currentDate = new Date(admission.admissionDate);
            // Add time if provided
            if (admission.admissionTime) {
              if (Array.isArray(admission.admissionTime)) {
                currentDate.setHours(
                  admission.admissionTime[0] || 0,
                  admission.admissionTime[1] || 0,
                  admission.admissionTime[2] || 0
                );
              } else if (typeof admission.admissionTime === 'string') {
                const timeParts = admission.admissionTime.split(':');
                if (timeParts.length >= 2) {
                  currentDate.setHours(
                    parseInt(timeParts[0]) || 0,
                    parseInt(timeParts[1]) || 0,
                    parseInt(timeParts[2]) || 0
                  );
                }
              }
            }
          } else {
            // Fallback: try to create date from value
            currentDate = new Date(admission.admissionDate);
          }
        } else {
          // No admissionDate, skip this admission
          continue;
        }
        
        // Validate the date
        if (isNaN(currentDate.getTime())) {
          console.warn(`Invalid date for admission ${admission.id}:`, admission.admissionDate);
          continue;
        }

        const existing = grouped.get(admission.patientId);
        if (!existing || new Date(existing._date) < currentDate) {
          grouped.set(admission.patientId, {
            ...admission,
            _date: currentDate,
          });
        }
      }

      // Convert Map to array and sort - single operation
      let latestAdmissions = Array.from(grouped.values())
        .sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
      
      console.log(`Processed ${latestAdmissions.length} unique patient admissions`);

      // Step 3: Ensure patientName is populated (especially for org-specific patients)
      try {
        // Collect unique patientIds where patientName is missing/empty
        const patientIdsNeedingName = Array.from(
          new Set(
            latestAdmissions
              .filter(a => !a.patientName && a.patientId)
              .map(a => a.patientId)
          )
        );

        if (patientIdsNeedingName.length > 0) {
          console.log(
            `[InPatientsTable] Enriching patient names for ${patientIdsNeedingName.length} patients`
          );

          // Batch fetch patients using existing helper
          const patientResults = await batchProcess(
            patientIdsNeedingName,
            async (pid) => {
              try {
                const patient = await getPatientById(pid);
                const name =
                  patient?.name ||
                  [patient?.firstName, patient?.lastName]
                    .filter(Boolean)
                    .join(" ")
                    .trim() ||
                  null;
                return { patientId: pid, name };
              } catch (e) {
                console.error(
                  `[InPatientsTable] Failed to fetch patient for id=${pid}`,
                  e
                );
                return { patientId: pid, name: null };
              }
            },
            10
          );

          const nameByPatientId = {};
          patientResults.forEach((res) => {
            if (res && res.patientId && res.name) {
              nameByPatientId[res.patientId] = res.name;
            }
          });

          latestAdmissions = latestAdmissions.map((admission) => ({
            ...admission,
            patientName:
              admission.patientName ||
              nameByPatientId[admission.patientId] ||
              admission.patientName,
          }));
        }
      } catch (nameErr) {
        console.error(
          "[InPatientsTable] Error while enriching patient names:",
          nameErr
        );
      }
      
      // Step 4: Show admissions immediately with basic data
      setAdmissions(latestAdmissions);
      const basicEnriched = latestAdmissions.map(admission => ({
        ...admission,
        assignedDoctor: null,
        creationDate: parseCreationDate(admission),
      }));
      setEnrichedAdmissions(basicEnriched);
      setFetching(false); // Show table immediately

      // Step 5: Fetch doctors and enrich first page in parallel
      const firstPageAdmissions = latestAdmissions.slice(0, rowsPerPage);
      setLoadingProgress({ loaded: 0, total: firstPageAdmissions.length, message: "Loading doctor details..." });
      
      const [allDoctors] = await Promise.all([
        getAllDoctors({ forceRefresh: false }).catch(() => [])
      ]);
      
      const doctorsLookup = buildDoctorsLookup(allDoctors);
      setDoctorsById(doctorsLookup);

      // Step 6: Enrich only the first page
      const enrichedFirstPage = await enrichAdmissions(firstPageAdmissions, doctorsLookup, patientDetailsMap);
      
      // Update only the enriched admissions
      const updatedEnriched = [...basicEnriched];
      const newCache = new Set();
      enrichedFirstPage.forEach((enriched) => {
        const index = updatedEnriched.findIndex(ea => ea.id === enriched.id);
        if (index !== -1) {
          updatedEnriched[index] = enriched;
          newCache.add(enriched.id);
        }
      });
      
      setEnrichedAdmissions(updatedEnriched);
      setEnrichmentCache(newCache);
      setLoadingProgress({ loaded: firstPageAdmissions.length, total: firstPageAdmissions.length, message: "" });
    } catch (err) {
      console.error("Error fetching admissions:", err);
      setLoadingProgress({ loaded: 0, total: 0, message: "Error loading data" });
    } finally {
      setFetching(false);
    }
  };

  // Enrich admissions when page changes (lazy loading)
  useEffect(() => {
    const enrichCurrentPage = async () => {
      if (admissions.length === 0 || fetching) return;
      
      const currentPageAdmissions = admissions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
      // Only enrich admissions that haven't been enriched yet
      const unenrichedAdmissions = currentPageAdmissions.filter(a => !enrichmentCache.has(a.id));
      
      if (unenrichedAdmissions.length > 0) {
        try {
          const allDoctors = await getAllDoctors({ forceRefresh: false }).catch(() => []);
          const doctorsById = {};
          const allDoctorsArray = Array.isArray(allDoctors) ? allDoctors : [];
          allDoctorsArray.forEach(doctor => {
            if (doctor.id) {
              doctorsById[doctor.id] = doctor;
            }
          });

          const enriched = await enrichAdmissions(unenrichedAdmissions, doctorsById, patientDetailsMap);
          setEnrichedAdmissions(prev => {
            const updated = [...prev];
            const newCache = new Set(enrichmentCache);
            enriched.forEach((enrichedAdmission) => {
              const index = updated.findIndex(ea => ea.id === enrichedAdmission.id);
              if (index !== -1) {
                updated[index] = enrichedAdmission;
                newCache.add(enrichedAdmission.id);
              }
            });
            setEnrichmentCache(newCache);
            return updated;
          });
        } catch (err) {
          console.error("Error enriching current page:", err);
        }
      }
    };

    enrichCurrentPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, admissions.length]);

  const toDateObj = (row) => {
    if (Array.isArray(row.admissionDate) && Array.isArray(row.admissionTime)) {
      const [y,m,d] = row.admissionDate; const [hh,mm,ss] = row.admissionTime;
      return new Date(y,(m||1)-1,d||1,hh||0,mm||0,ss||0);
    }
    if (Array.isArray(row.admissionDate)) { const [y,m,d]=row.admissionDate; return new Date(y,(m||1)-1,d||1); }
    return row._date ? new Date(row._date) : null;
  };

  const filteredRows = admissions.filter((row) => {
    const p = patientDetailsMap[row.patientId] || {};
    const enriched = enrichedAdmissions.find(ea => ea.id === row.id);
    
    // Global search across all fields
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const values = [
        row.id,
        row.patientId,
        row.patientName,
        p.email,
        p.mobile,
        p.gender,
        p.dob ? (() => {
          const age = Math.max(0, Math.floor((Date.now() - new Date(p.dob).getTime()) / (365.25*24*60*60*1000)));
          return age.toString();
        })() : null,
        row.status,
        enriched?.assignedDoctor,
        p.address,
        p.phone,
        p.aadhar,
      ]
        .filter(Boolean)
        .map((v)=>String(v).toLowerCase());
      
      // Also search in formatted admission date
      if (row.admissionDate) {
        let formattedDate = "";
        if (Array.isArray(row.admissionDate)) {
          formattedDate = row.admissionDate.join("-");
        } else if (typeof row.admissionDate === 'string') {
          formattedDate = row.admissionDate.split('T')[0];
        }
        if (formattedDate) {
          values.push(formattedDate.toLowerCase());
        }
      }
      
      if (!values.some((v)=>v.includes(q))) return false;
    }
    
    // Filter by any field (not just name)
    if (filterName.trim()) {
      const n = filterName.toLowerCase();
      const searchableFields = [
        row.id,
        row.patientId,
        row.patientName,
        p.email,
        p.mobile,
        p.gender,
        p.dob ? (() => {
          const age = Math.max(0, Math.floor((Date.now() - new Date(p.dob).getTime()) / (365.25*24*60*60*1000)));
          return age.toString();
        })() : null,
        row.status,
        enriched?.assignedDoctor,
        p.address,
        p.phone,
        p.aadhar,
      ]
        .filter(Boolean)
        .map((v)=>String(v).toLowerCase());
      
      // Also search in formatted admission date
      if (row.admissionDate) {
        let formattedDate = "";
        if (Array.isArray(row.admissionDate)) {
          formattedDate = row.admissionDate.join("-");
        } else if (typeof row.admissionDate === 'string') {
          formattedDate = row.admissionDate.split('T')[0];
        }
        if (formattedDate) {
          searchableFields.push(formattedDate.toLowerCase());
        }
      }
      
      if (!searchableFields.some((v)=>v.includes(n))) return false;
    }
    if (filterStatus) {
      const s = (row.status||"").toLowerCase();
      if (filterStatus === "admit" && s !== "admit") return false;
      if (filterStatus === "discharge" && s !== "discharge") return false;
    }
    if (fromDate || toDate) {
      const d = toDateObj(row);
      if (!d) return false;
      if (fromDate && d < new Date(fromDate)) return false;
      if (toDate) { const toEnd = new Date(toDate); toEnd.setHours(23,59,59,999); if (d > toEnd) return false; }
    }
    return true;
  });

  const summaryAdmissionIdsKey = filteredRows
    .map((row) => row.id)
    .filter(Boolean)
    .join("|");

  useEffect(() => {
    let cancelled = false;

    if (!summaryAdmissionIdsKey) return undefined;

    const missingIds = summaryAdmissionIdsKey
      .split("|")
      .filter(Boolean)
      .filter((id) => !fetchedAssignmentIdsRef.current.has(String(id)));

    if (missingIds.length === 0) return undefined;

    missingIds.forEach((id) => fetchedAssignmentIdsRef.current.add(String(id)));

    fetchAssignmentsByAdmissionIds(missingIds).then((fetchedAssignments) => {
      if (!cancelled && Object.keys(fetchedAssignments).length > 0) {
        setAssignmentsByAdmissionId((prev) => ({ ...prev, ...fetchedAssignments }));
      }
    }).catch((error) => {
      missingIds.forEach((id) => fetchedAssignmentIdsRef.current.delete(String(id)));
      if (!cancelled) {
        console.error("[InPatientsTable] Failed to load assignments for summary:", error);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [summaryAdmissionIdsKey]);

  const summaryRows = useMemo(
    () =>
      filteredRows.map((row) => {
        const enriched = enrichedAdmissions.find((ea) => ea.id === row.id);
        const assignment =
          enriched?.assignment ||
          assignmentsByAdmissionId[row.id] ||
          assignmentsByAdmissionId[String(row.id)] ||
          null;

        return {
          ...row,
          ...enriched,
          assignment,
          departmentId:
            enriched?.departmentId ||
            assignment?.departmentId ||
            row.departmentId,
        };
      }),
    [filteredRows, enrichedAdmissions, assignmentsByAdmissionId]
  );

  const periodBounds = parsePeriodBounds(fromDate, toDate);
  const periodLabel = formatPeriodLabel(fromDate, toDate);
  const periodSummary = useMemo(
    () =>
      computePatientPeriodSummary(
        buildIpdSummaryRecords({
          rows: summaryRows,
          allAdmissions: Array.isArray(admissionsCache) ? admissionsCache : [],
          doctorsById,
          departmentsById,
          periodStart: periodBounds.start,
          assignmentsByAdmissionId,
        })
      ),
    [
      summaryRows,
      admissionsCache,
      doctorsById,
      departmentsById,
      periodBounds.start,
      assignmentsByAdmissionId,
    ]
  );

  const clearFilters = () => { setSearchQuery(""); setFilterName(""); setFilterStatus(""); setFromDate(""); setToDate(""); };

  const handleExport = async () => {
    const rows = filteredRows || [];
    const headers = ["Admission Id","Patient Name","Admission Date","Admission Time","Admission Status","Creation Date","Assigned Doctor"];
    
    // Identify admissions that need doctor information
    const admissionIdsToEnrich = rows
      .map(r => r.id)
      .filter(Boolean)
      .filter(admissionId => {
        const enriched = enrichedAdmissions.find(ea => ea.id === admissionId);
        return !enriched || !enriched.assignedDoctor;
      });
    
    let exportEnrichedAdmissions = [...enrichedAdmissions];
    
    // Fetch missing doctor information if needed
    if (admissionIdsToEnrich.length > 0) {
      // Fetch doctors first
      const allDoctors = await getAllDoctors({ forceRefresh: false }).catch(() => []);
      const doctorsById = {};
      const allDoctorsArray = Array.isArray(allDoctors) ? allDoctors : [];
      allDoctorsArray.forEach(doctor => {
        if (doctor.id) {
          doctorsById[doctor.id] = doctor;
        }
      });
      
      // Fetch assignments and enrich admissions
      const admissionsToEnrich = rows.filter(r => admissionIdsToEnrich.includes(r.id));
      const enriched = await enrichAdmissions(admissionsToEnrich, doctorsById, patientDetailsMap);
      
      // Update exportEnrichedAdmissions with newly enriched data
      enriched.forEach((enrichedAdmission) => {
        const index = exportEnrichedAdmissions.findIndex(ea => ea.id === enrichedAdmission.id);
        if (index !== -1) {
          exportEnrichedAdmissions[index] = enrichedAdmission;
        } else {
          exportEnrichedAdmissions.push(enrichedAdmission);
        }
      });
    }

    const tableRows = rows.map((r) => {
      const enrichedAdmission = exportEnrichedAdmissions.find(ea => ea.id === r.id) || {};
      const creationDate = enrichedAdmission.creationDate ? new Date(enrichedAdmission.creationDate).toLocaleDateString() : "";
      const assignedDoctor = enrichedAdmission.assignedDoctor || "";
      
      let formattedDate = "";
      if (Array.isArray(r.admissionDate)) {
        formattedDate = r.admissionDate.join("-");
      } else if (r.admissionDate) {
        if (typeof r.admissionDate === 'string') {
          formattedDate = r.admissionDate.split('T')[0];
        } else if (r.admissionDate instanceof Date) {
          formattedDate = r.admissionDate.toISOString().split('T')[0];
        } else {
          formattedDate = String(r.admissionDate);
        }
      }
      
      let formattedTime = "";
      if (Array.isArray(r.admissionTime)) {
        formattedTime = r.admissionTime.join(":");
      } else if (r.admissionTime) {
        if (typeof r.admissionTime === 'string') {
          if (r.admissionTime.includes('T')) {
            formattedTime = r.admissionTime.split('T')[1]?.split('.')[0] || r.admissionTime;
          } else {
            formattedTime = r.admissionTime;
          }
        } else if (r.admissionTime instanceof Date) {
          formattedTime = r.admissionTime.toTimeString().split(' ')[0];
        } else {
          formattedTime = String(r.admissionTime);
        }
      }
      
      return [
        r.id,
        r.patientName,
        formattedDate,
        formattedTime,
        r.status,
        creationDate,
        assignedDoctor,
      ];
    });

    await downloadPatientTableExcel({
      filename: `in_patients_${fromDate || "all"}_${toDate || "all"}`,
      sheetName: "In Patients",
      organizationName: getOrganizationName(user),
      summaryTitle: "Inpatient Summary",
      periodLabel,
      fromDate: fromDate || null,
      toDate: toDate || null,
      summary: periodSummary,
      tableHeaders: headers,
      tableRows,
    });
  };

  const handleMenuClick = async (event, admission) => {
    const target = event.currentTarget;
    setSelectedAdmission(admission);
    setIsAssignedNurse(false);
    setIsAssignedDoctor(false);
    try {
      const assignment = await getAssignmentByAdmissionId(admission.id);
      const next = { ...admission, assignment };
      setSelectedAdmission(next);
      setIsAssignedNurse(computeIsAssignedNurse(next, assignment));
      
      // Handle async doctor assignment check
      const isDoctor = await isUserAssignedDoctor(next);
      setIsAssignedDoctor(isDoctor);
    } catch (err) {
      setIsAssignedNurse(false);
      setIsAssignedDoctor(false);
    } finally {
      // Open menu AFTER we have evaluated assignment to avoid double render
      setAnchorEl(target);
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedAdmission(null);
    setIsAssignedNurse(false);
    setIsAssignedDoctor(false);
  };

  const openModal = async (type, patientId, admissionId) => {
    setSelectedPatientId(patientId);
    setSelectedAdmissionId(admissionId);
    handleCloseMenu();
    try {
      const info = await getPatientById(patientId);
      setPatientInfo(info);
    } catch (err) {
      console.error("Failed to fetch patient info", err);
      setPatientInfo(null);
    }
    setModals((prev) => ({ ...prev, [type]: true }));
  };

  return (
    <Grid container>
      <Grid item xs={12}>
      <Paper sx={{ width: "100%" }}>
        <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
          <Box sx={TABLE_SEARCH_ROW_SX}>
            <TextField
              placeholder="Search"
              value={searchQuery}
              onChange={(e)=>setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={showFilters?"Hide filters":"Show filters"}>
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={()=>setShowFilters(v=>!v)}>
                <FilterAltIcon fontSize="small"/>
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                <DownloadIcon fontSize="small"/>
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh (force reload from server)">
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={() => {
                  setAdmissionsCache(null);
                  setCacheTimestamp(null);
                  fetchLatestAdmissions();
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {showFilters && (
          <Stack spacing={1} direction={{ xs:'column', sm:'row' }} alignItems={{ xs:'stretch', sm:'center' }} sx={{ mt:1 }}>
            <TextField size="small" label="Filter by any field" value={filterName} onChange={(e)=>setFilterName(e.target.value)} />
            <TextField size="small" label="Status" value={filterStatus} onChange={(e)=>setFilterStatus(e.target.value)} select sx={{ minWidth: 160 }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="admit">Admitted</MenuItem>
              <MenuItem value="discharge">Discharged</MenuItem>
            </TextField>
            <TextField size="small" label="From admission date" type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} InputLabelProps={{shrink:true}} />
            <TextField size="small" label="To admission date" type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)} InputLabelProps={{shrink:true}} />
            <Tooltip title="Clear filters">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                <ClearAllIcon fontSize="small"/>
              </IconButton>
            </Tooltip>
          </Stack>
          )}
        </Box>
        <TableContainer sx={{ maxHeight: "calc(100vh - 360px)" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell
                    key={column.id}
                    style={{ top: 57, minWidth: column.minWidth }}
                  >
                    {column.label}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {fetching ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Box sx={{ py: 3 }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        {loadingProgress.message || 
                         (loadingProgress.total > 0 
                          ? `Loading details... ${loadingProgress.loaded} / ${loadingProgress.total}`
                          : "Loading...")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : admissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <Alert>No Admissions Found</Alert>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    // Safely format admissionDate - handle array, string, or Date object
                    let admissionDate = "";
                    if (Array.isArray(row.admissionDate)) {
                      admissionDate = row.admissionDate.join("-");
                    } else if (row.admissionDate) {
                      // If it's a string or Date, format it appropriately
                      if (typeof row.admissionDate === 'string') {
                        // If it's an ISO string, extract date part
                        admissionDate = row.admissionDate.split('T')[0];
                      } else if (row.admissionDate instanceof Date) {
                        admissionDate = row.admissionDate.toISOString().split('T')[0];
                      } else {
                        admissionDate = String(row.admissionDate);
                      }
                    }
                    
                    // Safely format admissionTime - handle array, string, or Date object
                    let admissionTime = "";
                    if (Array.isArray(row.admissionTime)) {
                      admissionTime = row.admissionTime.join(":");
                    } else if (row.admissionTime) {
                      // If it's a string, use it directly
                      if (typeof row.admissionTime === 'string') {
                        // If it's an ISO string, extract time part
                        if (row.admissionTime.includes('T')) {
                          admissionTime = row.admissionTime.split('T')[1]?.split('.')[0] || row.admissionTime;
                        } else {
                          admissionTime = row.admissionTime;
                        }
                      } else if (row.admissionTime instanceof Date) {
                        admissionTime = row.admissionTime.toTimeString().split(' ')[0];
                      } else {
                        admissionTime = String(row.admissionTime);
                      }
                    }
                    
                    const p = patientDetailsMap[row.patientId] || {};
                    const enriched = enrichedAdmissions.find(ea => ea.id === row.id);
                    const age = p.dob ? Math.max(0, Math.floor((Date.now() - new Date(p.dob).getTime()) / (365.25*24*60*60*1000))) : null;
                    
                    return (
                      <TableRow hover key={row.id}>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.patientId || "-"}</TableCell>
                        <TableCell>
                          <span
                            style={{ color: "#568AF7", cursor: "pointer" }}
                            onClick={() => {
                              if (onPatientClick) {
                                onPatientClick(row.patientId);
                              } else {
                                navigate(`/ipd-dashboard/${row.patientId}`, { state: { tab: "1" } });
                              }
                            }}
                          >
                            {row.patientName}
                          </span>
                        </TableCell>
                        <TableCell>{p.email || "-"}</TableCell>
                        <TableCell>{p.mobile || "-"}</TableCell>
                        <TableCell>{p.gender || "-"}</TableCell>
                        <TableCell>{age !== null ? age : "-"}</TableCell>
                        <TableCell>{admissionDate}</TableCell>
                        <TableCell>{admissionTime}</TableCell>
                        <TableCell
                          style={{
                            fontWeight: 600,
                            color:
                              row.status?.toLowerCase() === "admit"
                                ? "green"
                                : row.status?.toLowerCase() === "discharge"
                                ? "red"
                                : "inherit",
                          }}
                        >
                        {row.status?.toLowerCase() === "admit"
                          ? "Admitted"
                          : row.status?.toLowerCase() === "discharge"
                          ? "Discharged"
                          : row.status
                        }
                        </TableCell>
                        <TableCell>
                          <IconButton onClick={(e) => handleMenuClick(e, row)}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={admissions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => setRowsPerPage(+e.target.value)}
        />
      </Paper>
      </Grid>

      {/* Menu Actions */}
      {/* <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
        {selectedAdmission && (
          <>
            {selectedAdmission.status?.toLowerCase() === "discharge" ? (
              <MenuItem onClick={() => openModal("edit", selectedAdmission.patientId, selectedAdmission.id)}>
                Edit Patient
              </MenuItem>
            ) : (
              <>
                <MenuItem onClick={() => openModal("edit", selectedAdmission.patientId, selectedAdmission.id)}>
                  Edit Patient
                </MenuItem>
                <MenuItem onClick={() => openModal("assignment", selectedAdmission.patientId, selectedAdmission.id)}>
                  + Patient Reassignment
                </MenuItem>
                {(user.roleName === "staff" || user.roleName === "doctor") && (
                  <>
                    <MenuItem onClick={() => openModal("doctorVisit", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Doctor / Staff Visits
                    </MenuItem>
                    <MenuItem onClick={() => openModal("treatment", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Treatment & Medicines
                    </MenuItem>
                    <MenuItem onClick={() => openModal("operation", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Operation & Surgeries
                    </MenuItem>
                    <MenuItem onClick={() => openModal("consumable", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Patient Consumables
                    </MenuItem>
                    <MenuItem onClick={() => openModal("discharge", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Discharge Formalities
                    </MenuItem>
                  </>
                )}
                {(user.roleName === "staff") && (
                  <MenuItem onClick={() => openModal("nursKardex", selectedAdmission.patientId, selectedAdmission.id)}>
                    + Nurse Kardex
                  </MenuItem>
                )}
              </>
            )}
          </>
        )}
      </Menu> */}

      {/* Menu Actions */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
        {selectedAdmission && (
          <>
            {/* Common for all, even if discharged */}
            <MenuItem onClick={() => openModal("edit", selectedAdmission.patientId, selectedAdmission.id)}>
              Edit Patient
            </MenuItem>

            {/* Emergency → IPD: reassignment before doctor/nurse assignment exists */}
            {selectedAdmission.status?.toLowerCase() !== "discharge" &&
              isAdmin &&
              isEmergencyConvertedIpd(selectedAdmission) &&
              !isAssignedDoctor &&
              !isAssignedNurse && (
              <MenuItem
                onClick={() =>
                  openModal("assignment", selectedAdmission.patientId, selectedAdmission.id)
                }
              >
                + Patient Reassignment
              </MenuItem>
            )}

            {/* New Admission option - only show if patient is discharged */}
            {selectedAdmission.status?.toLowerCase() === "discharge" && (
              <MenuItem
                onClick={async () => {
                  if (!selectedAdmission?.patientId) return;
                  
                  handleCloseMenu();
                  
                  try {
                    const info = await getPatientById(selectedAdmission.patientId);
                    setNewAdmissionPatientInfo(info);
                    setOpenNewAdmissionModal(true);
                  } catch (err) {
                    console.error("Failed to fetch patient info", err);
                    setNewAdmissionPatientInfo(null);
                    setOpenNewAdmissionModal(true);
                  }
                }}
              >
                New Admission
              </MenuItem>
            )}

            {/* If not discharged, show conditional menus */}
            {selectedAdmission.status?.toLowerCase() !== "discharge" && (
              <>
                {/* Reassignment accessible to everyone */}
               

                {/* ✅ Show only if user is assigned doctor */}
                {isAssignedDoctor && (
                  <>
                    {isAdmin && (
                     <MenuItem onClick={() => openModal("assignment", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Patient Reassignment
                    </MenuItem>
                    )}
                    <MenuItem onClick={() => openModal("prescription", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Add Prescription
                    </MenuItem>
                    <MenuItem onClick={() => openModal("doctorVisit", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Visits
                    </MenuItem>
                    {/* <MenuItem onClick={() => openModal("treatment", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Treatment & Medicines
                    </MenuItem> */}
                    {/* <MenuItem onClick={() => openModal("labtests", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Path Lab Tests
                    </MenuItem> */}
                    <MenuItem onClick={() => openModal("operation", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Operation & Surgeries
                    </MenuItem>
                    <MenuItem onClick={() => openModal("consumable", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Patient Consumables
                    </MenuItem>
                    <MenuItem onClick={() => openModal("discharge", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Discharge Formalities
                    </MenuItem>
                  </>
                )}

                {/* ✅ Show only if staff nurse is assigned */}
                {(roleName === "staff" || roleName.includes("nurse")) && (
                  <>
                     {isAdmin && isAssignedNurse && (
                  <MenuItem onClick={() => openModal("assignment", selectedAdmission.patientId, selectedAdmission.id)}>
                  + Patient Reassignment
                </MenuItem>
                )}
                    
                    <MenuItem onClick={() => openModal("doctorVisit", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Visits 
                    </MenuItem>
                    
                    
                    {/* <MenuItem onClick={() => openModal("treatment", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Treatment & Medicines
                    </MenuItem> */}
                    <MenuItem onClick={() => openModal("operation", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Operation & Surgeries
                    </MenuItem>
                    <MenuItem onClick={() => openModal("consumable", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Patient Consumables
                    </MenuItem>
                    <MenuItem onClick={() => openModal("discharge", selectedAdmission.patientId, selectedAdmission.id)}>
                      + Discharge Formalities
                    </MenuItem>
                  </>
                )}
                
                {/* Ensure Nurse Kardex appears whenever the logged-in user is the assigned nurse */}
                {isAssignedNurse && (
                  <MenuItem onClick={() => openModal("nursKardex", selectedAdmission.patientId, selectedAdmission.id)}>
                    + Nurse Kardex
                  </MenuItem>
                )}
              </>
            )}
          </>
        )}
      </Menu>

      {/* Modals */}
      <EditPatientModal
        open={modals.edit}
        onClose={() => setModals((m) => ({ ...m, edit: false }))}
        patient_info={patientInfo}
      />
      <PatientAssignmentModal
        open={modals.assignment}
        onClose={() => setModals((m) => ({ ...m, assignment: false }))}
        patientId={selectedPatientId}
        admissionId={selectedAdmissionId}
      />
      <PatientTreatmentModal
        open={modals.treatment}
        onClose={() => setModals((m) => ({ ...m, treatment: false }))}
        patientId={selectedPatientId}
        admissionId={selectedAdmissionId}
      />
      <LabtestModal
        open={modals.labtests}
        onClose={() => setModals((m) => ({ ...m, labtests: false }))}
        patientId={selectedPatientId}
        admissionId={selectedAdmissionId}
      />
      <PatientConsumableModal
        open={modals.consumable}
        onClose={() => setModals((m) => ({ ...m, consumable: false }))}
        patientId={selectedPatientId}
        admissionId={selectedAdmissionId}
      />
      <DoctorVisitsModal
        open={modals.doctorVisit}
        onClose={() => setModals((m) => ({ ...m, doctorVisit: false }))}
        patientId={selectedPatientId}
        admissionId={selectedAdmissionId}
      />
      <PatientOperationModal
        open={modals.operation}
        onClose={() => setModals((m) => ({ ...m, operation: false }))}
        patientId={selectedPatientId}
        admissionId={selectedAdmissionId}
      />
      <PatientDischargeModal
        open={modals.discharge}
        onClose={() => setModals((m) => ({ ...m, discharge: false }))}
        patientId={selectedPatientId}
        admissionId={selectedAdmissionId}
        refreshData={fetchLatestAdmissions}
      />
      <NurseKardex
        open={modals.nursKardex}
        onClose={() => setModals((m) => ({ ...m, nursKardex: false }))}
        patientId={selectedPatientId}
        admissionId={selectedAdmissionId}
      />
      <IpdPrescriptionModal
        open={modals.prescription}
        onClose={() => setModals((m) => ({ ...m, prescription: false }))}
        patientId={selectedPatientId}
        patientName={selectedAdmission?.patientName}
        admissionId={selectedAdmissionId}
        admission={selectedAdmission}
        doctorId={selectedAdmission?.assignment?.doctorId || user?.id}
      />
      {/* New Admission Modal */}
      <NewAdmissionModal
        open={openNewAdmissionModal}
        onClose={async () => {
          setOpenNewAdmissionModal(false);
          setNewAdmissionPatientInfo(null);
          await fetchLatestAdmissions();
        }}
        patient_info={newAdmissionPatientInfo}
      />
    </Grid>
  );
}

export default PatientsTable;